import { useState, useEffect } from 'react';

const flowSteps = [
  { id: 1, icon: 'ri-user-add-line', label: 'Uzman Başvurur', color: '#ff4d4d' },
  { id: 2, icon: 'ri-scan-line', label: 'Sistem Analiz Eder', color: '#ff5a3d' },
  { id: 3, icon: 'ri-bar-chart-2-line', label: 'Skor Hesaplanır', color: '#ff6b47' },
  { id: 4, icon: 'ri-eye-line', label: 'Görünürlük Artar', color: '#ff7c42' },
  { id: 5, icon: 'ri-user-voice-line', label: 'Müşteri Talebi', color: '#ff8c42' },
  { id: 6, icon: 'ri-git-merge-line', label: 'Akıllı Eşleşme', color: '#ffa94d' },
  { id: 7, icon: 'ri-scissors-line', label: 'Hizmet Verilir', color: '#ffcc66' },
  { id: 8, icon: 'ri-star-line', label: 'Müşteri Puanlar', color: '#ffa94d' },
  { id: 9, icon: 'ri-refresh-line', label: 'Skor Güncellenir', color: '#ff8c42' },
  { id: 10, icon: 'ri-rocket-line', label: 'Daha Fazla İş', color: '#ff4d4d' },
];

const SystemFlowHero: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % flowSteps.length);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden mb-6"
      style={{
        background: 'linear-gradient(135deg, #0d0d14 0%, #12101a 50%, #0a0f14 100%)',
        border: '1px solid rgba(255,77,77,0.2)',
        minHeight: '220px',
      }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(255,77,77,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10 p-6">
        <div className="text-center mb-5">
          <h2
            className="text-lg font-bold mb-1"
            style={{
              background: 'linear-gradient(90deg, #ff4d4d, #ff8c42)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            BiMakas Sistem Akışı
          </h2>
          <p className="text-xs" style={{ color: '#6b7280' }}>Uçtan uca otomatik döngü — sürekli çalışır, sürekli büyür</p>
        </div>

        {/* Steps Row */}
        <div className="flex items-center justify-between overflow-x-auto pb-2 gap-1">
          {flowSteps.map((step, idx) => {
            const isActive = idx === activeStep;
            const isPrev = idx < activeStep || (activeStep === 0 && idx === flowSteps.length - 1);
            return (
              <div key={step.id} className="flex items-center flex-shrink-0">
                {/* Step card */}
                <div
                  className="flex flex-col items-center gap-1.5 transition-all duration-500"
                  style={{
                    transform: isActive ? 'scale(1.15) translateY(-4px)' : 'scale(1)',
                  }}
                >
                  <div
                    className="relative w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-500"
                    style={{
                      background: isActive
                        ? `linear-gradient(135deg, ${step.color}33, ${step.color}18)`
                        : isPrev
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(255,255,255,0.03)',
                      border: isActive
                        ? `1.5px solid ${step.color}`
                        : `1px solid rgba(255,255,255,0.08)`,
                      boxShadow: isActive ? `0 0 16px ${step.color}44` : 'none',
                    }}
                  >
                    <i
                      className={`${step.icon} text-base transition-all duration-500`}
                      style={{ color: isActive ? step.color : isPrev ? '#4b5563' : '#374151' }}
                    ></i>
                    {isActive && (
                      <div
                        className="absolute inset-0 rounded-xl animate-ping"
                        style={{ background: `${step.color}15`, animationDuration: '1.5s' }}
                      />
                    )}
                  </div>
                  <span
                    className="text-center transition-all duration-500 leading-tight"
                    style={{
                      color: isActive ? '#ffffff' : isPrev ? '#4b5563' : '#374151',
                      fontSize: '9px',
                      maxWidth: '60px',
                      fontWeight: isActive ? 600 : 400,
                    }}
                  >
                    {step.label}
                  </span>
                  <div
                    className="w-1.5 h-1.5 rounded-full transition-all duration-500"
                    style={{
                      background: isActive ? step.color : isPrev ? '#374151' : '#1f2937',
                      boxShadow: isActive ? `0 0 6px ${step.color}` : 'none',
                    }}
                  />
                </div>

                {/* Arrow */}
                {idx < flowSteps.length - 1 && (
                  <div className="flex items-center mx-0.5 flex-shrink-0" style={{ marginTop: '-16px' }}>
                    <div
                      className="transition-all duration-500"
                      style={{
                        width: '18px',
                        height: '2px',
                        background:
                          idx < activeStep
                            ? `linear-gradient(90deg, ${flowSteps[idx].color}, ${flowSteps[idx + 1].color})`
                            : 'rgba(255,255,255,0.1)',
                        borderRadius: '1px',
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          right: '-3px',
                          top: '-3px',
                          width: '0',
                          height: '0',
                          borderTop: '4px solid transparent',
                          borderBottom: '4px solid transparent',
                          borderLeft: idx < activeStep ? `5px solid ${flowSteps[idx + 1].color}` : '5px solid rgba(255,255,255,0.1)',
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Loop arrow back */}
                {idx === flowSteps.length - 1 && (
                  <div className="flex items-center ml-1 flex-shrink-0" style={{ marginTop: '-16px' }}>
                    <i className="ri-corner-down-left-line text-xs" style={{ color: '#ff4d4d' }}></i>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Active step description */}
        <div className="mt-4 text-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{
              background: `linear-gradient(135deg, ${flowSteps[activeStep].color}22, ${flowSteps[activeStep].color}11)`,
              border: `1px solid ${flowSteps[activeStep].color}44`,
              color: flowSteps[activeStep].color,
            }}
          >
            <i className={`${flowSteps[activeStep].icon}`}></i>
            Adım {activeStep + 1}: {flowSteps[activeStep].label}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SystemFlowHero;
